import React from 'react'
// libs
import { useMutation } from '@apollo/client/react'
import { useSnackbar } from 'notistack'
// hooks
import { useModalData } from 'hooks/index'
// components
import { Button, BUTTON_LAYOUTS, CREATE_BUTTON_STYLE, Modal } from 'components/UI'
import { Header } from 'components/Modals/ScaleOptionModal/Create'
import { CloseIcon } from 'components/UI/Icons'
// constants
import { Modals } from 'constants/modals'
import { PAPER_PROPS } from './constants'
// graphql
import { AUTH_TWO_FACTOR_ADMIN_RESET_MUTATION } from 'graphql/auth/twoFactor/mutations'
// views
import { ButtonGroup, ContentContainer, HelperText } from './views'
// utils
import { handleError } from 'utils/errors/handlers'

const TwoFactorAdminResetModal: React.FC = () => {
  const {
    hideModal,
    modalData: { isOpen, userId, companyId, refetch }
  } = useModalData({ modalType: Modals.TWO_FACTOR_ADMIN_RESET_MODAL })

  const { enqueueSnackbar } = useSnackbar()

  const [resetMutation, { loading }] = useMutation(AUTH_TWO_FACTOR_ADMIN_RESET_MUTATION)

  const handleReset = async () => {
    try {
      await resetMutation({
        variables: {
          userId,
          companyId: companyId || undefined
        }
      })

      enqueueSnackbar('Two-factor authentication has been reset for the user.', {
        variant: 'success',
        autoHideDuration: 5000
      })

      if (refetch) {
        refetch()
      }

      hideModal()
    } catch (error) {
      handleError(error, 'TwoFactorAdminResetModal.handleReset')
    }
  }

  return (
    <Modal width='sm' isOpen={isOpen} hideModal={hideModal} PaperProps={PAPER_PROPS}>
      <Header>
        <span>Reset User's 2FA</span>
        <CloseIcon right={0} onClick={hideModal} />
      </Header>

      <ContentContainer>
        <p>Are you sure you want to reset two-factor authentication for this user?</p>
        <HelperText>
          This will completely remove 2FA from their account. They will need to set it up again if they want to
          re-enable it.
        </HelperText>

        <ButtonGroup>
          <Button layout={BUTTON_LAYOUTS.WHITE} onClick={hideModal} type='button'>
            Cancel
          </Button>

          <Button layout={BUTTON_LAYOUTS.PURPLE} onClick={handleReset} disabled={loading} style={CREATE_BUTTON_STYLE}>
            Reset 2FA
          </Button>
        </ButtonGroup>
      </ContentContainer>
    </Modal>
  )
}

export default TwoFactorAdminResetModal
